import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, FormControlLabel, MenuItem, Select, Typography,
} from '@mui/material';
import { formatPhone, personName } from '../../voice/callFormat';
import { missedCallsApi, errorMessage } from './missedCallsApi';
import { formatDateTime, uploadedDay, whoLines } from './missedCallFormat';

/**
 * 통화 연결 (plan §6.11) — tie a call that was already placed to this row.
 *
 * Everything the row shows about calls is read from `call_logs.missed_call_id`, and that is otherwise only written
 * when a call is dialled from the row's own icon. An admin who rings the parent straight from the softphone ends
 * up with a call nothing links to the row; this is the way back.
 *
 * The server answers with every unattached call in the window, not just this number's: a callback often goes out
 * to the other parent, or to a number typed by hand. The dialog then shows the matching ones first and filters to
 * them by default (번호가 같은 통화만), because in practice the row's own number is what you want — but it falls
 * back to the whole list when nothing matches, rather than presenting an empty dialog that reads as "nothing to
 * attach". Non-matching calls always carry 번호 다름; with the filter on, none are shown, so the chip disappears
 * by itself.
 */
const DAY_CHOICES = [7, 14, 30, 90];

/** Under a minute reads in seconds — Math.round() turned a 20-second call into "통화 0분". */
const spoken = (s) => (s < 60 ? `${s}초` : `${Math.round(s / 60)}분`);

const outcome = (c) => {
    if (c.status === 'completed') return c.duration_seconds ? `통화 ${spoken(c.duration_seconds)}` : '통화';
    if (c.status === 'no_answer') return '무응답';
    if (c.status === 'busy') return '통화 중';
    if (c.status === 'failed') return '실패';
    if (c.status === 'canceled') return '취소';
    return c.status || '';
};

const AttachCallDialog = ({ row, open, onClose, onAttached }) => {
    const [days, setDays] = useState(7);
    const [calls, setCalls] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [busyId, setBusyId] = useState(null);
    const [alsoStudent, setAlsoStudent] = useState(true);
    const [onlyMatching, setOnlyMatching] = useState(true);

    const named = whoLines(row).filter((l) => l.student || l.teacher);
    const rowStudents = (row?.who?.students) || [];
    // Only offered when the row names exactly one student — the server refuses to guess between siblings anyway.
    const theStudent = rowStudents.length === 1 && !row?.who?.teacher ? rowStudents[0] : null;

    const load = useCallback(async () => {
        if (!row) return;
        setLoading(true);
        setError('');
        try {
            const data = await missedCallsApi.attachableCalls(row.id, days);
            setCalls(data.calls || []);
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setLoading(false);
        }
    }, [row?.id, days]);   // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { if (open) load(); }, [open, load]);
    useEffect(() => { if (open) { setAlsoStudent(true); setOnlyMatching(true); } }, [open]);

    if (!row) return null;

    const matching = calls.filter((c) => c.number_matches);
    const others = calls.filter((c) => !c.number_matches);
    // A default-on filter must never produce an empty dialog while candidates exist: the admin would read that as
    // "nothing to attach" and close it, when the call they want is sitting there under another number (a callback
    // to the other parent, or a number typed by hand). So when nothing matches, show everything and say why.
    const fellBack = onlyMatching && matching.length === 0 && calls.length > 0;
    // Matched first either way, recency preserved inside each group (the server already answers newest-first).
    const visible = onlyMatching && !fellBack ? matching : [...matching, ...others];

    const attach = async (call, move = false) => {
        setBusyId(call.id);
        setError('');
        try {
            // Only meaningful for a call with nobody on it: the server ignores it otherwise.
            const withStudent = alsoStudent && theStudent && !call.student && !call.teacher;
            const out = await missedCallsApi.attachCall(row.id, call.id, { move, attachStudent: Boolean(withStudent) });
            if (onAttached) onAttached(out);
            onClose();
        } catch (err) {
            if (err?.response?.status === 409) {
                // Already on another row: say so and let them move it deliberately.
                setError('이 통화는 이미 다른 행에 연결되어 있습니다. "다른 행에서 가져오기"를 누르면 이 행으로 옮깁니다.');
                setCalls((prev) => prev.map((c) => (c.id === call.id ? { ...c, needs_move: true } : c)));
            } else {
                setError(errorMessage(err));
            }
        } finally {
            setBusyId(null);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" disableEnforceFocus>
            <DialogTitle>통화 연결</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary">
                    {uploadedDay(row)} #{row.daily_seq} · {formatPhone(row.phone_number)}
                    {/* Only when the row names someone: whoLines always answers, falling back to "모름", which
                        would hang a pointless 모름 off every header. */}
                    {named.length ? ` · ${named.map((l) => l.text).join(', ')}` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    소프트폰에서 바로 건 통화를 이 행에 연결합니다. 연결하면 시작/종료 시간과 AI 요약, 시도 횟수가 이 행에 표시됩니다.
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                    <Typography variant="body2">기간</Typography>
                    <Select size="small" value={days} onChange={(e) => setDays(Number(e.target.value))}>
                        {DAY_CHOICES.map((d) => <MenuItem key={d} value={d}>{`최근 ${d}일`}</MenuItem>)}
                    </Select>
                    <Button size="small" onClick={load} disabled={loading}>새로고침</Button>
                    <FormControlLabel
                        sx={{ ml: 0.5 }}
                        control={(
                            <Checkbox
                                size="small"
                                checked={onlyMatching}
                                onChange={(e) => setOnlyMatching(e.target.checked)}
                                inputProps={{ 'aria-label': '번호가 같은 통화만' }}
                            />
                        )}
                        label={<Typography variant="body2">{`번호가 같은 통화만${matching.length ? ` (${matching.length})` : ''}`}</Typography>}
                    />
                </Box>

                {theStudent && (
                    <FormControlLabel
                        sx={{ mt: 1 }}
                        control={<Checkbox checked={alsoStudent} onChange={(e) => setAlsoStudent(e.target.checked)} />}
                        label={
                            <Typography variant="body2">
                                이 통화를 <b>{personName(theStudent)}</b> 학생카드에도 기록 (AI 요약 메모 → 학생카드반영)
                            </Typography>
                        }
                    />
                )}

                {error && <Alert severity="warning" sx={{ mt: 1.5 }}>{error}</Alert>}

                <Divider sx={{ my: 1.5 }} />

                {loading && <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /></Box>}
                {!loading && fellBack && (
                    <Alert severity="info" sx={{ mb: 1.5 }}>
                        이 번호로 건 통화가 없어 전체를 표시합니다. 번호가 다른 통화는 번호 다름으로 표시됩니다.
                    </Alert>
                )}
                {!loading && !calls.length && (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        연결할 수 있는 통화가 없습니다. 아직 어느 행에도 연결되지 않은 통화만 보입니다 — 기간을 넓혀 보세요.
                    </Typography>
                )}

                {!loading && visible.map((c) => (
                    <Box
                        key={c.id}
                        sx={{
                            display: 'flex', alignItems: 'center', gap: 1, py: 1,
                            borderBottom: 1, borderColor: 'divider',
                        }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {formatPhone(c.to_number) || c.to_number || '번호 없음'}
                                {!c.number_matches && <Chip size="small" color="warning" variant="outlined" label="번호 다름" sx={{ ml: 0.75 }} />}
                                {c.has_summary && <Chip size="small" variant="outlined" label="AI 요약" sx={{ ml: 0.75 }} />}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {formatDateTime(c.started_at || c.created_at)} · {outcome(c)}
                                {c.admin ? ` · ${personName(c.admin)}` : ''}
                                {c.student ? ` · ${personName(c.student)}` : ''}
                                {c.teacher ? ` · ${personName(c.teacher)} 선생님` : ''}
                            </Typography>
                        </Box>
                        <Button
                            size="small"
                            variant={c.needs_move ? 'outlined' : 'contained'}
                            color={c.needs_move ? 'warning' : 'primary'}
                            disabled={busyId === c.id}
                            onClick={() => attach(c, Boolean(c.needs_move))}
                            sx={{ flexShrink: 0, textTransform: 'none' }}
                        >
                            {c.needs_move ? '다른 행에서 가져오기' : '연결'}
                        </Button>
                    </Box>
                ))}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>닫기</Button>
            </DialogActions>
        </Dialog>
    );
};

export default AttachCallDialog;
